import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { useToast } from '@/hooks/use-toast';
import { DateRange } from 'react-day-picker';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { fetchInvoiceReport, saveInvoiceReport } from '@/services/invoiceReportService';
import { Skeleton } from '@/components/ui/skeleton';
import { Navbar } from '@/components/Navbar';
import ExportInvoiceReportModal from '@/components/invoices/ExportInvoiceReportModal'; // Importez le nouveau composant
import { useIsMobile } from '@/hooks/use-mobile';

// Interface for report data
export interface InvoiceReportData {
  summary: {
    totalRevenue: number;
    totalPaid: number;
    totalPending: number;
    totalInvoices: number;
  };
  monthly: Array<{
    month: string;
    revenue: number;
    paid: number;
    pending: number;
    count: number;
  }>;
  paymentMethods: {
    card: number;
    cash: number;
    pending: number;
  };
  topStudents: Array<{
    id: string;
    name: string;
    totalSpent: number;
    invoiceCount: number;
  }>;
}

const InvoiceReport: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const totalTaxes: { [key: string]: number } = {};

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    to: new Date(),
  });

  // Fetch data when dateRange changes
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoiceReport', dateRange?.from, dateRange?.to],
    queryFn: () => {
      if (dateRange?.from && dateRange?.to) {
        return fetchInvoiceReport(dateRange);
      }
      return Promise.resolve(null);
    },
  });

  const saveReportMutation = useMutation({
    mutationFn: saveInvoiceReport,
    onSuccess: () => {
      toast({
        title: t('reports.saveSuccess'),
        description: t('reports.saveSuccessDescription'),
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('reports.saveError'),
        description: error instanceof Error ? error.message : t('reports.saveErrorDescription'),
      });
    },
  });

  const handleSaveReport = () => {
    if (data) {
      saveReportMutation.mutate({
        data,
        dateRange,
        userId: user?.id || '',
      });
    }
  };

  const downloadReport = () => {
    // This would be implemented to generate a downloadable report
    toast({
      title: t('reports.downloadStarted'),
      description: t('reports.downloadStartedDescription'),
    });
  };

  // No need to explicitly call refetch when dateRange changes, as the queryKey includes dateRange
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="container py-10 mt-16">
          <h1 className="text-2xl font-bold mb-6"></h1>
          <div className="space-y-4">
            <Skeleton className="h-[200px] w-full" />
            <Skeleton className="h-[400px] w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 container py-8 mt-16">
        <Card>
          <div className="flex-1 container py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
              <CardTitle>{t('reports.invoiceReport')}</CardTitle>
              <div className="flex flex-col sm:flex-row gap-4 mt-4 md:mt-0">
                <DatePickerWithRange
                  date={dateRange}
                  setDate={setDateRange}
                  className="w-full sm:w-auto"
                />
                {/*<Button variant="outline" onClick={() => refetch()} className="w-full sm:w-auto">
                  {t('reports.filterButton')}
                </Button>*/}
              </div>
            </div>

            {data && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>{t('reports.totalRevenue')} ({t('reports.beforeTax')})</CardDescription>
                      <CardTitle className="text-2xl">
                        {new Intl.NumberFormat(undefined, {
                          style: 'currency',
                          currency: user?.currency_code || 'USD',
                        }).format(data.summary.totalRevenue)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>{t('reports.totalPaid')} ({t('reports.includingTax')})</CardDescription>
                      <CardTitle className="text-2xl">
                        {new Intl.NumberFormat(undefined, {
                          style: 'currency',
                          currency: user?.currency_code || 'USD',
                        }).format(data.summary.totalPaid)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>{t('reports.totalPending')} ({t('reports.includingTax')})</CardDescription>
                      <CardTitle className="text-2xl">
                        {new Intl.NumberFormat(undefined, {
                          style: 'currency',
                          currency: user?.currency_code || 'USD',
                        }).format(data.summary.totalPending)}
                      </CardTitle>
                    </CardHeader>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>{t('reports.totalInvoices')}</CardDescription>
                      <CardTitle className="text-2xl">{data.summary.totalInvoices}</CardTitle>
                    </CardHeader>
                  </Card>
                </div>

                <Tabs defaultValue="monthly" className="mb-6">
                  <TabsList>
                    {!isMobile && (<TabsTrigger value="overview">{t('reports.overview')}</TabsTrigger>)}
                    <TabsTrigger value="monthly">{t('reports.monthlyBreakdown')}</TabsTrigger>
                    <TabsTrigger value="payments">{t('reports.paymentMethods')}</TabsTrigger>
                    <TabsTrigger value="students">{t('reports.topStudents')}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('reports.revenueOverview')}</CardTitle>
                        <CardDescription>{t('reports.revenueDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[400px] w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={data.monthly}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" />
                              <YAxis />
                              <Tooltip formatter={(value) =>
                                new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: user?.currency_code || 'USD',
                                }).format(Number(value))
                              } />
                              <Legend />
                              <Bar dataKey="revenue" name={t('reports.revenue')} fill="#8B5CF6" />
                              <Bar dataKey="paid" name={t('reports.paid')} fill="#0EA5E9" />
                              <Bar dataKey="pending" name={t('reports.pending')} fill="#F97316" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="monthly">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('reports.monthlyBreakdown')}</CardTitle>
                        <CardDescription>{t('reports.monthlyDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('reports.month')}</TableHead>
                              <TableHead>{t('reports.revenue')} ({t('reports.beforeTax')})</TableHead>
                              <TableHead>{t('reports.taxes')}</TableHead>
                              <TableHead>{t('reports.expenses')}</TableHead>
                              <TableHead>{t('reports.paid')}</TableHead>
                              <TableHead>{t('reports.pending')}</TableHead>
                              <TableHead>{t('reports.invoiceCount')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.monthly.map((month) => (
                              <TableRow key={month.month}>
                                <TableCell>{t(`reports.months.${month.month}`)}</TableCell>
                                <TableCell>
                                  {new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: user?.currency_code || 'USD',
                                  }).format(month.revenue)}
                                </TableCell>
                                <TableCell>
                                  {month.taxes && Object.keys(month.taxes).length > 0
                                    ? Object.entries(month.taxes).map(([taxName, amount]) => (
                                    <div key={taxName} className="text-sm">
                                    {taxName && `${taxName}: `}
                                    {new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: user?.currency_code || 'USD',
                                    }).format(Number(amount))}
                                    </div>
                                    ))
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  {month.expenses && Object.keys(month.expenses).length > 0
                                    ? new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: user?.currency_code || 'USD',
                                  }).format(month.totalExpenses)
                                    : '-'}
                                </TableCell>


                                <TableCell>
                                  {new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: user?.currency_code || 'USD',
                                  }).format(month.paid)}
                                </TableCell>
                                <TableCell>
                                  {new Intl.NumberFormat(undefined, {
                                    style: 'currency',
                                    currency: user?.currency_code || 'USD',
                                  }).format(month.pending)}
                                </TableCell>
                                <TableCell>{month.count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                          <TableFooter>
                            <TableRow>
                              <TableCell>{t('reports.total')}</TableCell>
                              <TableCell>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: user?.currency_code || 'USD',
                                }).format(data.summary.totalRevenue)}
                              </TableCell>
                              <TableCell>
                                {Object.entries(data.summary.totalTaxes).map(([taxName, amount]) => (
                                  <div key={taxName}>
                                    {taxName && `${taxName}: `}
                                    {new Intl.NumberFormat(undefined, {
                                      style: 'currency',
                                      currency: user?.currency_code || 'USD',
                                    }).format(Number(amount))}
                                  </div>
                                ))}
                              </TableCell>
                              <TableCell>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: user?.currency_code || 'USD',
                                }).format(data.summary.totalExpenses)}
                              </TableCell>
                              <TableCell>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: user?.currency_code || 'USD',
                                }).format(data.summary.totalPaid)}
                              </TableCell>
                              <TableCell>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: user?.currency_code || 'USD',
                                }).format(data.summary.totalPending)}
                              </TableCell>
                              <TableCell>{data.summary.totalInvoices}</TableCell>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="payments">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('reports.paymentMethods')}</CardTitle>
                        <CardDescription>{t('reports.paymentMethodsDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('reports.paymentMethod')}</TableHead>
                              <TableHead className="text-right">{t('reports.totalAmount')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {data.paymentMethods && Object.keys(data.paymentMethods).length > 0 ? (
                              Object.entries(data.paymentMethods).map(([method, amount]) => (
                                <TableRow key={method}>
                                  <TableCell>{t(`reports.${method}`)}</TableCell>
                                  <TableCell className="text-right">
                                    {new Intl.NumberFormat(undefined, {
                                      style: 'currency',
                                      currency: user?.currency_code || 'USD',
                                    }).format(Number(amount))}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} className="text-center text-muted-foreground">
                                  {t('reports.noPaymentData')}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </TabsContent>


                  <TabsContent value="students">
                    <Card>
                        <CardHeader>
                        <CardTitle>{t('reports.topStudents')}</CardTitle>
                        <CardDescription>{t('reports.topStudentsDescription')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                        <Table>
                            <TableHeader>
                            <TableRow>
                                <TableHead>{t('reports.studentName')}</TableHead>
                                <TableHead>{t('reports.totalSpent')}</TableHead>
                                <TableHead>{t('reports.invoiceCount')}</TableHead>
                            </TableRow>
                            </TableHeader>
                            <TableBody>
                            {data.topStudents && typeof data.topStudents === 'object' && Object.keys(data.topStudents).length > 0 ? (
                                Object.values(data.topStudents).map((student:any) => (
                                <TableRow key={student.id}>
                                    <TableCell>{student.name}</TableCell>
                                    <TableCell>
                                    {new Intl.NumberFormat(undefined, {
                                        style: 'currency',
                                        currency: user?.currency_code || 'USD',
                                    }).format(student.totalSpent)}
                                    </TableCell>
                                    <TableCell>{student.invoiceCount}</TableCell>
                                </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                <TableCell colSpan={3}>No student data available</TableCell>
                                </TableRow>
                            )}
                            </TableBody>
                        </Table>
                        </CardContent>
                    </Card>
                    </TabsContent>
                </Tabs>

                <div className={`flex ${isMobile ? 'justify-center' : 'justify-end'} space-x-4 mt-4`}>
                  <Button onClick={() => setIsExportModalOpen(true)}>
                    <Download className="mr-2 h-4 w-4" />
                    {t('reports.downloadReport')}
                  </Button>
                  
                  {/*<Button  onClick={downloadReport}>
                    <Download className="mr-2 h-4 w-4" />
                    {t('reports.downloadReport')}
                  </Button>*/}

                  {/**<Button onClick={handleSaveReport} disabled={saveReportMutation.isPending}>
                    <Save className="mr-2 h-4 w-4" />
                    {saveReportMutation.isPending ? t('reports.saving') : t('reports.saveReport')}
                  </Button> */}
                </div>
              </>
            )}
          </div>
        </Card>
      </main>
        <ExportInvoiceReportModal
          open={isExportModalOpen}
          onOpenChange={setIsExportModalOpen}
          reportData={data}
          dateRange={dateRange}
          currency={user?.currency_code || 'USD'}
        />
      </div>
  );
};

export default InvoiceReport;
